import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Sidebar } from "./components/Sidebar";
import { TopBar } from "./components/TopBar";
import { Dashboard } from "./components/Dashboard";
import { BusinessDetail } from "./components/BusinessDetail";
import { CompanyDetail } from "./components/CompanyDetail";
import { ProjectDetail } from "./components/ProjectDetail";
import { BusinessForm } from "./components/forms/BusinessForm";
import { CompanyForm } from "./components/forms/CompanyForm";
import { ProjectForm } from "./components/forms/ProjectForm";
import { SearchResults } from "./components/SearchResults";
import { TeamMemberManager } from "./components/TeamMemberManager";
import { UserManager } from "./components/UserManager";
import { NotificationCenter } from "./components/NotificationCenter";
import { TaskWorkspace } from "./components/TaskWorkspace";
import { OpsDashboard } from "./components/OpsDashboard";
import { OpsSiteDetail } from "./components/OpsSiteDetail";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 15000,
});

function LoginGate({ onLogin, loading }) {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    await onLogin({
      username: username.trim(),
      password,
      totp_code: totpCode.trim(),
    });
  };

  return (
    <div className="login-screen">
      <div className="login-panel">
        <div className="login-panel-header">
          <p className="login-eyebrow">Restricted Access</p>
          <h1>Batsho Management App</h1>
          <p className="login-copy">Sign in with your password and six-digit authenticator code to unlock the dashboard.</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <label className="login-field">
            <span>Username</span>
            <input autoComplete="username" className="login-input" disabled={loading} onChange={(event) => setUsername(event.target.value)} type="text" value={username} />
          </label>
          <label className="login-field">
            <span>Password</span>
            <input autoComplete="current-password" className="login-input" disabled={loading} onChange={(event) => setPassword(event.target.value)} type="password" value={password} />
          </label>
          <label className="login-field">
            <span>Authenticator Code</span>
            <input autoComplete="one-time-code" className="login-input" disabled={loading} inputMode="numeric" maxLength={6} onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" type="text" value={totpCode} />
          </label>
          <button className="login-submit" disabled={loading} type="submit">{loading ? "Checking..." : "Sign In"}</button>
        </form>
      </div>
    </div>
  );
}

function SharedProjectView({ payload }) {
  if (!payload) {
    return <div className="empty-state"><div className="text-lg font-mono">Loading shared project...</div></div>;
  }

  const project = payload.project || {};
  const tasks = project.tasks || [];

  return (
    <div className="content-area">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="card p-6">
          <h1 className="font-heading text-3xl font-black uppercase tracking-tight text-zinc-900">{project.name || "Shared Project"}</h1>
          <p className="mt-2 text-sm text-zinc-500">{payload.company?.name} / {payload.business?.name}</p>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-zinc-600">
            <span>Status: {project.status || "Unknown"}</span>
            <span>Priority: {project.priority || "Unknown"}</span>
            <span>Lead: {project.lead || "Unassigned"}</span>
          </div>
        </div>
        <div className="card p-6">
          <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">Project Tasks</h2>
          <div className="space-y-4">
            {tasks.length === 0 ? <p className="text-sm text-zinc-400">No tasks available.</p> : tasks.map((task) => (
              <div className="border border-zinc-200 bg-white p-4" key={task.id}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-zinc-900">{task.title}</h3>
                    <p className="mt-1 text-sm text-zinc-500">{task.assignee || "Unassigned"} · {task.due_date || "No due date"}</p>
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-400">{task.status}</span>
                </div>
                {task.notes ? <p className="mt-3 text-sm text-zinc-700">{task.notes}</p> : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


function ScopeToolbar({ hierarchy, scopeCompanyId, scopeBusinessId, scopeProjectId, onCompanyChange, onBusinessChange, onProjectChange, onClear }) {
  const companies = hierarchy || [];
  const businesses = scopeCompanyId
    ? (companies.find((company) => company.id === scopeCompanyId)?.businesses || [])
    : companies.flatMap((company) => company.businesses || []);
  const projects = scopeBusinessId
    ? (businesses.find((business) => business.id === scopeBusinessId)?.projects || [])
    : businesses.flatMap((business) => business.projects || []);
  const hasActiveScope = Boolean(scopeCompanyId || scopeBusinessId || scopeProjectId);

  return (
    <section className="card mb-6 p-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">Portfolio Scope</h2>
          <p className="mt-1 text-sm text-zinc-500">Filter Dashboard, Task Workspace, and Operations to a specific company, business, or project.</p>
        </div>
        <div className="grid gap-3 md:grid-cols-3 xl:min-w-[760px]">
          <label className="space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">Company</span>
            <select className="h-10 w-full rounded-none border border-zinc-300 bg-white px-3 text-sm" onChange={(event) => onCompanyChange(event.target.value)} value={scopeCompanyId}>
              <option value="">All companies</option>
              {companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
            </select>
          </label>
          <label className="space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">Business</span>
            <select className="h-10 w-full rounded-none border border-zinc-300 bg-white px-3 text-sm" onChange={(event) => onBusinessChange(event.target.value)} value={scopeBusinessId}>
              <option value="">All businesses</option>
              {businesses.map((business) => <option key={business.id} value={business.id}>{business.name}</option>)}
            </select>
          </label>
          <label className="space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">Project</span>
            <select className="h-10 w-full rounded-none border border-zinc-300 bg-white px-3 text-sm" onChange={(event) => onProjectChange(event.target.value)} value={scopeProjectId}>
              <option value="">All projects</option>
              {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
            </select>
          </label>
        </div>
      </div>
      {hasActiveScope ? <div className="mt-4 flex justify-end"><button className="btn-secondary" onClick={onClear} type="button">Clear Scope</button></div> : null}
    </section>
  );
}

function App() {
  const authFlowRef = useRef({ sessionCheckId: 0, loginAttemptId: 0 });
  const publicShareToken = useMemo(() => {
    const match = window.location.pathname.match(/^\/public\/share\/([^/]+)$/);
    return match ? match[1] : null;
  }, []);

  const [hierarchy, setHierarchy] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activity, setActivity] = useState([]);
  const [taskBoard, setTaskBoard] = useState([]);
  const [opsSites, setOpsSites] = useState([]);
  const [opsDashboardData, setOpsDashboardData] = useState(null);
  const [opsSuppliers, setOpsSuppliers] = useState([]);
  const [opsProcurement, setOpsProcurement] = useState([]);
  const [opsAssets, setOpsAssets] = useState([]);
  const [opsZones, setOpsZones] = useState([]);
  const [opsServices, setOpsServices] = useState([]);
  const [opsSessions, setOpsSessions] = useState([]);
  const [opsPayments, setOpsPayments] = useState([]);
  const [opsMemberships, setOpsMemberships] = useState([]);
  const [opsMaintenance, setOpsMaintenance] = useState([]);
  const [opsInventory, setOpsInventory] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [publicShare, setPublicShare] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [scopeCompanyId, setScopeCompanyId] = useState("");
  const [scopeBusinessId, setScopeBusinessId] = useState("");
  const [scopeProjectId, setScopeProjectId] = useState("");
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [selectedOpsEntityType, setSelectedOpsEntityType] = useState(null);
  const [selectedOpsEntityId, setSelectedOpsEntityId] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [view, setView] = useState("dashboard");
  const [taskFilter, setTaskFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authStatus, setAuthStatus] = useState("checking");
  const [authLoading, setAuthLoading] = useState(false);
  const [aiBriefLoading, setAiBriefLoading] = useState(false);

  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [showBusinessForm, setShowBusinessForm] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showTeamMemberManager, setShowTeamMemberManager] = useState(false);
  const [showUserManager, setShowUserManager] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const resetWorkspace = useCallback(() => {
    setHierarchy([]);
    setDashboardData(null);
    setTeamMembers([]);
    setUsers([]);
    setNotifications([]);
    setActivity([]);
    setTaskBoard([]);
    setOpsSites([]);
    setOpsDashboardData(null);
    setOpsSuppliers([]);
    setOpsProcurement([]);
    setOpsAssets([]);
    setOpsZones([]);
    setOpsServices([]);
    setOpsSessions([]);
    setOpsPayments([]);
    setOpsMemberships([]);
    setOpsMaintenance([]);
    setOpsInventory([]);
    setCurrentUser(null);
    setSelectedCompanyId(null);
    setSelectedBusinessId(null);
    setSelectedProjectId(null);
    setScopeCompanyId("");
    setScopeBusinessId("");
    setScopeProjectId("");
    setSelectedSiteId(null);
    setSelectedOpsEntityType(null);
    setSelectedOpsEntityId(null);
    setSelectedTaskId(null);
    setView("dashboard");
    setTaskFilter("all");
    setSearchQuery("");
    setSearchResults(null);
    setLoading(false);
  }, []);

  const handleUnauthorized = useCallback(() => {
    setAuthStatus("unauthenticated");
    resetWorkspace();
  }, [resetWorkspace]);

  const isUnauthorized = (error) => error?.response?.status === 401;

  const fetchHierarchy = useCallback(async () => {
    const response = await apiClient.get("/hierarchy");
    setHierarchy(response.data);
    return response.data;
  }, []);

  const fetchDashboard = useCallback(async () => {
    const response = await apiClient.get("/dashboard");
    setDashboardData(response.data);
    return response.data;
  }, []);

  const fetchTeamMembers = useCallback(async () => {
    const response = await apiClient.get("/team-members");
    setTeamMembers(response.data);
    return response.data;
  }, []);

  const fetchUsers = useCallback(async () => {
    const response = await apiClient.get("/users");
    setUsers(response.data);
    return response.data;
  }, []);

  const fetchNotifications = useCallback(async () => {
    const response = await apiClient.get("/notifications");
    setNotifications(response.data);
    return response.data;
  }, []);

  const fetchActivity = useCallback(async () => {
    const response = await apiClient.get("/activity");
    setActivity(response.data);
    return response.data;
  }, []);

  const fetchTaskBoard = useCallback(async () => {
    const response = await apiClient.get("/tasks/board");
    setTaskBoard(response.data);
    return response.data;
  }, []);

  const fetchOpsSites = useCallback(async () => {
    const response = await apiClient.get("/ops/sites");
    setOpsSites(response.data);
    return response.data;
  }, []);

  const fetchOpsDashboard = useCallback(async () => {
    const response = await apiClient.get("/ops/dashboard");
    setOpsDashboardData(response.data);
    return response.data;
  }, []);

  const fetchOpsSuppliers = useCallback(async () => {
    const response = await apiClient.get("/ops/suppliers");
    setOpsSuppliers(response.data);
    return response.data;
  }, []);

  const fetchOpsProcurement = useCallback(async () => {
    const response = await apiClient.get("/ops/procurement");
    setOpsProcurement(response.data);
    return response.data;
  }, []);

  const fetchOpsAssets = useCallback(async () => {
    const response = await apiClient.get("/ops/assets");
    setOpsAssets(response.data);
    return response.data;
  }, []);

  const fetchOpsZones = useCallback(async () => {
    const response = await apiClient.get("/ops/zones");
    setOpsZones(response.data);
    return response.data;
  }, []);

  const fetchOpsServices = useCallback(async () => {
    const response = await apiClient.get("/ops/services");
    setOpsServices(response.data);
    return response.data;
  }, []);

  const fetchOpsSessions = useCallback(async () => {
    const response = await apiClient.get("/ops/sessions");
    setOpsSessions(response.data);
    return response.data;
  }, []);

  const fetchOpsPayments = useCallback(async () => {
    const response = await apiClient.get("/ops/payments");
    setOpsPayments(response.data);
    return response.data;
  }, []);

  const fetchOpsMemberships = useCallback(async () => {
    const response = await apiClient.get("/ops/memberships");
    setOpsMemberships(response.data);
    return response.data;
  }, []);

  const fetchOpsMaintenance = useCallback(async () => {
    const response = await apiClient.get("/ops/maintenance");
    setOpsMaintenance(response.data);
    return response.data;
  }, []);

  const fetchOpsInventory = useCallback(async () => {
    const response = await apiClient.get("/ops/inventory");
    setOpsInventory(response.data);
    return response.data;
  }, []);

  const loadData = useCallback(async (user) => {
    setLoading(true);
    try {
      const requests = [
        fetchHierarchy(),
        fetchDashboard(),
        fetchTeamMembers(),
        fetchNotifications(),
        fetchActivity(),
        fetchTaskBoard(),
        fetchOpsSites(),
        fetchOpsDashboard(),
        fetchOpsSuppliers(),
        fetchOpsProcurement(),
        fetchOpsAssets(),
        fetchOpsZones(),
        fetchOpsServices(),
        fetchOpsSessions(),
        fetchOpsPayments(),
        fetchOpsMemberships(),
        fetchOpsMaintenance(),
        fetchOpsInventory(),
      ];
      if (user?.role === "admin") {
        requests.push(fetchUsers());
      } else {
        setUsers([]);
      }
      await Promise.all(requests);
    } finally {
      setLoading(false);
    }
  }, [fetchActivity, fetchDashboard, fetchHierarchy, fetchNotifications, fetchOpsAssets, fetchOpsDashboard, fetchOpsInventory, fetchOpsMaintenance, fetchOpsMemberships, fetchOpsPayments, fetchOpsProcurement, fetchOpsServices, fetchOpsSessions, fetchOpsSites, fetchOpsSuppliers, fetchOpsZones, fetchTaskBoard, fetchTeamMembers, fetchUsers]);

  const checkSession = useCallback(async () => {
    if (publicShareToken) return;
    const sessionCheckId = authFlowRef.current.sessionCheckId + 1;
    authFlowRef.current.sessionCheckId = sessionCheckId;
    setAuthStatus("checking");
    try {
      const response = await apiClient.get("/auth/session");
      if (
        authFlowRef.current.sessionCheckId !== sessionCheckId ||
        authFlowRef.current.loginAttemptId > 0
      ) {
        return;
      }
      setCurrentUser(response.data);
      setAuthStatus("authenticated");
      await loadData(response.data);
    } catch (error) {
      if (authFlowRef.current.sessionCheckId !== sessionCheckId) {
        return;
      }
      if (!isUnauthorized(error)) {
        console.error("Error checking session:", error);
      }
      setAuthStatus("unauthenticated");
      resetWorkspace();
    } finally {
      authFlowRef.current.sessionCheckId = Math.max(authFlowRef.current.sessionCheckId, sessionCheckId);
    }
  }, [loadData, publicShareToken, resetWorkspace]);

  useEffect(() => {
    if (!publicShareToken) {
      checkSession();
      return;
    }
    (async () => {
      try {
        const response = await apiClient.get(`/public/share/${publicShareToken}`);
        setPublicShare(response.data);
      } catch (error) {
        console.error("Error loading shared project:", error);
        setPublicShare({ project: { name: "Shared project unavailable", tasks: [] }, company: {}, business: {} });
      }
    })();
  }, [publicShareToken]);

  const runAction = async (callback, fallbackMessage = "Request failed") => {
    try {
      return await callback();
    } catch (error) {
      if (isUnauthorized(error)) {
        handleUnauthorized();
        return null;
      }
      console.error(error);
      toast.error(error?.response?.data?.detail || fallbackMessage);
      throw error;
    }
  };

  const handleLogin = async (credentials) => {
    authFlowRef.current.loginAttemptId += 1;
    setAuthLoading(true);
    try {
      const response = await apiClient.post("/auth/login", credentials);
      setCurrentUser(response.data);
      setAuthStatus("authenticated");
      toast.success("Signed in successfully");
    } catch (error) {
      console.error("Error signing in:", error);
      setAuthStatus("unauthenticated");
      toast.error(error?.response?.data?.detail || "Sign in failed");
    } finally {
      authFlowRef.current.loginAttemptId = 0;
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    if (authStatus !== "authenticated" || !currentUser) {
      return;
    }

    (async () => {
      try {
        await loadData(currentUser);
      } catch (error) {
        if (isUnauthorized(error)) {
          handleUnauthorized();
          return;
        }
        console.error("Error loading authenticated workspace:", error);
        toast.error("Signed in, but some dashboard data could not be loaded.");
      }
    })();
  }, [authStatus, currentUser, handleUnauthorized, loadData]);

  const handleLogout = useCallback(async () => {
    try {
      await apiClient.post("/auth/logout");
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      handleUnauthorized();
    }
  }, [handleUnauthorized]);

  const handleGenerateAiBrief = async () => {
    setAiBriefLoading(true);
    try {
      const response = await apiClient.get("/reports/smart?include_ai=true");
      setDashboardData((current) => (current ? { ...current, smart_report: response.data } : current));
      if (response.data.ai_brief) {
        toast.success("AI brief generated");
      } else if (response.data.ai_brief_error) {
        toast.error(response.data.ai_brief_error);
      }
    } catch (error) {
      if (isUnauthorized(error)) {
        handleUnauthorized();
        return;
      }
      console.error(error);
      toast.error("Failed to generate AI brief");
    } finally {
      setAiBriefLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults(null);
      setView("dashboard");
      return;
    }
    await runAction(async () => {
      const response = await apiClient.get(`/search?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
      setView("search");
    }, "Search failed");
  };

  const resetSearch = () => {
    setSearchQuery("");
    setSearchResults(null);
  };

  const handleSelectCompany = (companyId) => {
    setSelectedCompanyId(companyId);
    setSelectedBusinessId(null);
    setSelectedProjectId(null);
    setSelectedSiteId(null);
    setSelectedOpsEntityType(null);
    setSelectedOpsEntityId(null);
    setSelectedTaskId(null);
    setView("company");
    setSidebarOpen(false);
    resetSearch();
  };

  const handleSelectBusiness = (businessId, companyId) => {
    setSelectedCompanyId(companyId);
    setSelectedBusinessId(businessId);
    setSelectedProjectId(null);
    setSelectedSiteId(null);
    setSelectedOpsEntityType(null);
    setSelectedOpsEntityId(null);
    setSelectedTaskId(null);
    setView("business");
    setSidebarOpen(false);
    resetSearch();
  };

  const handleSelectProject = (projectId, businessId, companyId, taskId = null) => {
    setSelectedCompanyId(companyId);
    setSelectedBusinessId(businessId);
    setSelectedProjectId(projectId);
    setSelectedSiteId(null);
    setSelectedOpsEntityType(null);
    setSelectedOpsEntityId(null);
    setSelectedTaskId(taskId);
    setView("project");
    setSidebarOpen(false);
    resetSearch();
  };

  const handleSelectSite = (siteId, businessId, companyId, entityType = null, entityId = null) => {
    setSelectedCompanyId(companyId);
    setSelectedBusinessId(businessId);
    setSelectedProjectId(null);
    setSelectedSiteId(siteId);
    setSelectedOpsEntityType(entityType);
    setSelectedOpsEntityId(entityId);
    setSelectedTaskId(null);
    setView("ops-site");
    setSidebarOpen(false);
    resetSearch();
  };

  const handleSelectOpsEntity = (entityType, entityId, siteId, businessId, companyId) => {
    const resolvedSiteId = siteId || opsSites.find((item) => item.business_id === businessId)?.id || null;
    if (resolvedSiteId) {
      handleSelectSite(resolvedSiteId, businessId, companyId, entityType, entityId);
      return;
    }
    if (businessId && companyId) {
      handleSelectBusiness(businessId, companyId);
      return;
    }
    handleGoToOps();
  };


  const handleScopeCompanyChange = (companyId) => {
    setScopeCompanyId(companyId);
    setScopeBusinessId("");
    setScopeProjectId("");
  };

  const handleScopeBusinessChange = (businessId) => {
    if (!businessId) {
      setScopeBusinessId("");
      setScopeProjectId("");
      return;
    }
    let parentCompanyId = "";
    for (const company of hierarchy) {
      const business = (company.businesses || []).find((item) => item.id === businessId);
      if (business) {
        parentCompanyId = company.id;
        break;
      }
    }
    setScopeCompanyId(parentCompanyId);
    setScopeBusinessId(businessId);
    setScopeProjectId("");
  };

  const handleScopeProjectChange = (projectId) => {
    if (!projectId) {
      setScopeProjectId("");
      return;
    }
    for (const company of hierarchy) {
      for (const business of company.businesses || []) {
        const project = (business.projects || []).find((item) => item.id === projectId);
        if (project) {
          setScopeCompanyId(company.id);
          setScopeBusinessId(business.id);
          setScopeProjectId(projectId);
          return;
        }
      }
    }
  };

  const clearPortfolioScope = () => {
    setScopeCompanyId("");
    setScopeBusinessId("");
    setScopeProjectId("");
  };

  const handleGoToDashboard = () => {
    setSelectedCompanyId(null);
    setSelectedBusinessId(null);
    setSelectedProjectId(null);
    setSelectedSiteId(null);
    setSelectedOpsEntityType(null);
    setSelectedOpsEntityId(null);
    setSelectedTaskId(null);
    setView("dashboard");
    setSidebarOpen(false);
    resetSearch();
  };

  const handleGoToTasks = () => {
    setSelectedCompanyId(null);
    setSelectedBusinessId(null);
    setSelectedProjectId(null);
    setSelectedSiteId(null);
    setSelectedOpsEntityType(null);
    setSelectedOpsEntityId(null);
    setSelectedTaskId(null);
    setView("tasks");
    setSidebarOpen(false);
    resetSearch();
  };

  const handleGoToOps = () => {
    setSelectedCompanyId(null);
    setSelectedBusinessId(null);
    setSelectedProjectId(null);
    setSelectedSiteId(null);
    setSelectedOpsEntityType(null);
    setSelectedOpsEntityId(null);
    setSelectedTaskId(null);
    setView("ops-dashboard");
    setSidebarOpen(false);
    resetSearch();
  };

  const handleOpenDashboardProject = (projectId, taskId = null) => {
    for (const company of hierarchy) {
      for (const business of company.businesses || []) {
        const project = (business.projects || []).find((item) => item.id === projectId);
        if (project) {
          handleSelectProject(projectId, business.id, company.id, taskId);
          return;
        }
      }
    }
    toast.error("Project could not be found");
  };

  const postAndReload = async (request, successMessage) => {
    const result = await runAction(request);
    if (successMessage) toast.success(successMessage);
    await loadData(currentUser);
    return result?.data || null;
  };

  const handleCreateUser = (data) => postAndReload(() => apiClient.post("/users", data), "User created");
  const handleUpdateUser = (userId, data) => postAndReload(() => apiClient.put(`/users/${userId}`, data), "User updated");
  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Delete this user?")) return;
    await postAndReload(() => apiClient.delete(`/users/${userId}`), "User deleted");
  };

  const handleMarkNotificationRead = async (notificationId) => {
    await postAndReload(() => apiClient.post(`/notifications/${notificationId}/read`), "Notification updated");
  };

  const handleCreateTeamMember = (data) => postAndReload(() => apiClient.post("/team-members", data), "Team member added");
  const handleUpdateTeamMember = (memberId, data) => postAndReload(() => apiClient.put(`/team-members/${memberId}`, data), "Team member updated");
  const handleDeleteTeamMember = async (memberId) => {
    if (!window.confirm("Delete this team member?")) return;
    await postAndReload(() => apiClient.delete(`/team-members/${memberId}`), "Team member deleted");
  };
  const handleCreateCompany = async (data) => { await postAndReload(() => apiClient.post("/companies", data), "Company created successfully"); setShowCompanyForm(false); };
  const handleUpdateCompany = async (id, data) => { await postAndReload(() => apiClient.put(`/companies/${id}`, data), "Company updated successfully"); setEditingCompany(null); };
  const handleDeleteCompany = async (id) => { if (!window.confirm("Delete this company and all nested businesses/projects?")) return; await postAndReload(() => apiClient.delete(`/companies/${id}`), "Company deleted successfully"); if (selectedCompanyId === id) handleGoToDashboard(); };
  const handleCreateBusiness = async (data) => { await postAndReload(() => apiClient.post("/businesses", data), "Business created successfully"); setShowBusinessForm(false); };
  const handleUpdateBusiness = async (id, data) => { await postAndReload(() => apiClient.put(`/businesses/${id}`, data), "Business updated successfully"); setEditingBusiness(null); };
  const handleDeleteBusiness = async (id) => { if (!window.confirm("Delete this business and all its projects?")) return; await postAndReload(() => apiClient.delete(`/businesses/${id}`), "Business deleted successfully"); if (selectedBusinessId === id) handleSelectCompany(selectedCompanyId); };
  const handleCreateProject = async (data) => { await postAndReload(() => apiClient.post("/projects", data), "Project created successfully"); setShowProjectForm(false); };
  const handleUpdateProject = async (id, data) => { await postAndReload(() => apiClient.put(`/projects/${id}`, data), "Project updated successfully"); setEditingProject(null); };
  const handleDeleteProject = async (id) => { if (!window.confirm("Delete this project?")) return; await postAndReload(() => apiClient.delete(`/projects/${id}`), "Project deleted successfully"); if (selectedProjectId === id) handleSelectBusiness(selectedBusinessId, selectedCompanyId); };
  const handleCreateTask = (projectId, data) => postAndReload(() => apiClient.post(`/projects/${projectId}/tasks`, data), "Task created successfully");
  const handleUpdateTask = (projectId, taskId, data) => postAndReload(() => apiClient.put(`/projects/${projectId}/tasks/${taskId}`, data), "Task updated successfully");
  const handleDeleteTask = async (projectId, taskId) => { if (!window.confirm("Delete this task?")) return; await postAndReload(() => apiClient.delete(`/projects/${projectId}/tasks/${taskId}`), "Task deleted successfully"); };
  const handleCreateSubtask = (projectId, taskId, data) => postAndReload(() => apiClient.post(`/projects/${projectId}/tasks/${taskId}/subtasks`, data), "Subtask created successfully");
  const handleUpdateSubtask = (projectId, taskId, subtaskId, data) => postAndReload(() => apiClient.put(`/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}`, data));
  const handleDeleteSubtask = (projectId, taskId, subtaskId) => postAndReload(() => apiClient.delete(`/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}`), "Subtask deleted successfully");
  const handleCreateComment = (projectId, taskId, data) => postAndReload(() => apiClient.post(`/projects/${projectId}/tasks/${taskId}/comments`, data), "Comment added");
  const handleUpdateComment = (projectId, taskId, commentId, data) => postAndReload(() => apiClient.put(`/projects/${projectId}/tasks/${taskId}/comments/${commentId}`, data), "Comment updated");
  const handleDeleteComment = (projectId, taskId, commentId) => postAndReload(() => apiClient.delete(`/projects/${projectId}/tasks/${taskId}/comments/${commentId}`), "Comment deleted");

  const handleUploadAttachment = async (projectId, taskId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    await postAndReload(() => apiClient.post(`/projects/${projectId}/tasks/${taskId}/attachments`, formData, { headers: { "Content-Type": "multipart/form-data" } }), "Attachment uploaded");
  };

  const handleDeleteAttachment = (projectId, taskId, attachmentId) => postAndReload(() => apiClient.delete(`/projects/${projectId}/tasks/${taskId}/attachments/${attachmentId}`), "Attachment deleted");
  const handleCreateShareLink = (projectId) => postAndReload(() => apiClient.post(`/projects/${projectId}/share-links`), "Share link created");
  const handleGenerateProjectSummary = async (projectId) => {
    const result = await runAction(() => apiClient.post(`/projects/${projectId}/ai-summary`), "Failed to generate project summary");
    return result?.data || null;
  };
  const handleCreateOpsSite = (data) => postAndReload(() => apiClient.post("/ops/sites", data), "Operations site created");
  const handleCreateOpsSupplier = (data) => postAndReload(() => apiClient.post("/ops/suppliers", data), "Supplier created");
  const handleUpdateOpsSupplier = (supplierId, data) => postAndReload(() => apiClient.put(`/ops/suppliers/${supplierId}`, data), "Supplier updated");
  const handleDeleteOpsSupplier = (supplierId) => postAndReload(() => apiClient.delete(`/ops/suppliers/${supplierId}`), "Supplier deleted");
  const handleCreateOpsProcurement = (data) => postAndReload(() => apiClient.post("/ops/procurement", data), "Procurement request created");
  const handleUpdateOpsProcurement = (requestId, data) => postAndReload(() => apiClient.put(`/ops/procurement/${requestId}`, data), "Procurement request updated");
  const handleDeleteOpsProcurement = (requestId) => postAndReload(() => apiClient.delete(`/ops/procurement/${requestId}`), "Procurement request deleted");
  const handleCreateOpsProcurementLine = (requestId, data) => postAndReload(() => apiClient.post(`/ops/procurement/${requestId}/lines`, data), "Procurement line created");
  const handleUpdateOpsProcurementLine = (lineId, data) => postAndReload(() => apiClient.put(`/ops/procurement/lines/${lineId}`, data), "Procurement line updated");
  const handleDeleteOpsProcurementLine = (lineId) => postAndReload(() => apiClient.delete(`/ops/procurement/lines/${lineId}`), "Procurement line deleted");
  const handleCreateOpsSupplierQuote = (lineId, data) => postAndReload(() => apiClient.post(`/ops/procurement/lines/${lineId}/quotes`, data), "Supplier quote created");
  const handleUpdateOpsSupplierQuote = (quoteId, data) => postAndReload(() => apiClient.put(`/ops/procurement/quotes/${quoteId}`, data), "Supplier quote updated");
  const handleDeleteOpsSupplierQuote = (quoteId) => postAndReload(() => apiClient.delete(`/ops/procurement/quotes/${quoteId}`), "Supplier quote deleted");
  const handleCreateOpsAsset = (data) => postAndReload(() => apiClient.post("/ops/assets", data), "Asset created");
  const handleUpdateOpsAsset = (assetId, data) => postAndReload(() => apiClient.put(`/ops/assets/${assetId}`, data), "Asset updated");
  const handleDeleteOpsAsset = (assetId) => postAndReload(() => apiClient.delete(`/ops/assets/${assetId}`), "Asset deleted");
  const handleCreateOpsZone = (data) => postAndReload(() => apiClient.post("/ops/zones", data), "Zone created");
  const handleUpdateOpsZone = (zoneId, data) => postAndReload(() => apiClient.put(`/ops/zones/${zoneId}`, data), "Zone updated");
  const handleDeleteOpsZone = (zoneId) => postAndReload(() => apiClient.delete(`/ops/zones/${zoneId}`), "Zone deleted");
  const handleCreateOpsService = (data) => postAndReload(() => apiClient.post("/ops/services", data), "Service created");
  const handleUpdateOpsService = (serviceId, data) => postAndReload(() => apiClient.put(`/ops/services/${serviceId}`, data), "Service updated");
  const handleDeleteOpsService = (serviceId) => postAndReload(() => apiClient.delete(`/ops/services/${serviceId}`), "Service deleted");
  const handleCreateOpsSession = (data) => postAndReload(() => apiClient.post("/ops/sessions", data), "Operational session created");
  const handleUpdateOpsSession = (sessionId, data) => postAndReload(() => apiClient.put(`/ops/sessions/${sessionId}`, data), "Operational session updated");
  const handleDeleteOpsSession = (sessionId) => postAndReload(() => apiClient.delete(`/ops/sessions/${sessionId}`), "Operational session deleted");
  const handleCreateOpsPayment = (data) => postAndReload(() => apiClient.post("/ops/payments", data), "Payment created");
  const handleUpdateOpsPayment = (paymentId, data) => postAndReload(() => apiClient.put(`/ops/payments/${paymentId}`, data), "Payment updated");
  const handleDeleteOpsPayment = (paymentId) => postAndReload(() => apiClient.delete(`/ops/payments/${paymentId}`), "Payment deleted");
  const handleCreateOpsMembership = (data) => postAndReload(() => apiClient.post("/ops/memberships", data), "Account created");
  const handleUpdateOpsMembership = (membershipId, data) => postAndReload(() => apiClient.put(`/ops/memberships/${membershipId}`, data), "Account updated");
  const handleDeleteOpsMembership = (membershipId) => postAndReload(() => apiClient.delete(`/ops/memberships/${membershipId}`), "Account deleted");
  const handleCreateOpsMaintenance = (data) => postAndReload(() => apiClient.post("/ops/maintenance", data), "Maintenance work order created");
  const handleUpdateOpsMaintenance = (workOrderId, data) => postAndReload(() => apiClient.put(`/ops/maintenance/${workOrderId}`, data), "Maintenance work order updated");
  const handleDeleteOpsMaintenance = (workOrderId) => postAndReload(() => apiClient.delete(`/ops/maintenance/${workOrderId}`), "Maintenance work order deleted");
  const handleCreateOpsMaintenanceLog = (workOrderId, data) => postAndReload(() => apiClient.post(`/ops/maintenance/${workOrderId}/logs`, data), "Maintenance log created");
  const handleUpdateOpsMaintenanceLog = (logId, data) => postAndReload(() => apiClient.put(`/ops/maintenance/logs/${logId}`, data), "Maintenance log updated");
  const handleDeleteOpsMaintenanceLog = (logId) => postAndReload(() => apiClient.delete(`/ops/maintenance/logs/${logId}`), "Maintenance log deleted");
  const handleCreateOpsInventoryItem = (data) => postAndReload(() => apiClient.post("/ops/inventory", data), "Inventory item created");
  const handleUpdateOpsInventoryItem = (itemId, data) => postAndReload(() => apiClient.put(`/ops/inventory/${itemId}`, data), "Inventory item updated");
  const handleDeleteOpsInventoryItem = (itemId) => postAndReload(() => apiClient.delete(`/ops/inventory/${itemId}`), "Inventory item deleted");
  const handleCreateOpsInventoryMovement = (itemId, data) => postAndReload(() => apiClient.post(`/ops/inventory/${itemId}/movements`, data), "Inventory movement created");
  const handleUpdateOpsInventoryMovement = (movementId, data) => postAndReload(() => apiClient.put(`/ops/inventory/movements/${movementId}`, data), "Inventory movement updated");
  const handleDeleteOpsInventoryMovement = (movementId) => postAndReload(() => apiClient.delete(`/ops/inventory/movements/${movementId}`), "Inventory movement deleted");

  const companyLookup = useMemo(() => {
    const map = {};
    for (const company of hierarchy) {
      map[company.id] = company;
    }
    return map;
  }, [hierarchy]);

  const businessLookup = useMemo(() => {
    const map = {};
    for (const company of hierarchy) {
      for (const business of company.businesses || []) {
        map[business.id] = { ...business, company_id: company.id, company_name: company.name };
      }
    }
    return map;
  }, [hierarchy]);

  const projectLookup = useMemo(() => {
    const map = {};
    for (const company of hierarchy) {
      for (const business of company.businesses || []) {
        for (const project of business.projects || []) {
          map[project.id] = { ...project, business_id: business.id, business_name: business.name, company_id: company.id, company_name: company.name };
        }
      }
    }
    return map;
  }, [hierarchy]);

  const effectiveScopeProjectId = scopeProjectId || "";
  const effectiveScopeBusinessId = effectiveScopeProjectId ? (projectLookup[effectiveScopeProjectId]?.business_id || scopeBusinessId || "") : (scopeBusinessId || "");
  const effectiveScopeCompanyId = effectiveScopeBusinessId ? (businessLookup[effectiveScopeBusinessId]?.company_id || scopeCompanyId || "") : (scopeCompanyId || "");
  const hasPortfolioScope = Boolean(effectiveScopeCompanyId || effectiveScopeBusinessId || effectiveScopeProjectId);

  const matchesPortfolioScope = useCallback((companyId, businessId, projectId = "") => {
    if (effectiveScopeProjectId) return projectId === effectiveScopeProjectId;
    if (effectiveScopeBusinessId) return businessId === effectiveScopeBusinessId;
    if (effectiveScopeCompanyId) return companyId === effectiveScopeCompanyId;
    return true;
  }, [effectiveScopeBusinessId, effectiveScopeCompanyId, effectiveScopeProjectId]);

  const scopedHierarchy = useMemo(() => {
    return hierarchy
      .map((company) => {
        if (effectiveScopeCompanyId && company.id !== effectiveScopeCompanyId) return null;
        const businesses = (company.businesses || [])
          .map((business) => {
            if (effectiveScopeBusinessId && business.id !== effectiveScopeBusinessId) return null;
            const projects = (business.projects || []).filter((project) => !effectiveScopeProjectId || project.id === effectiveScopeProjectId);
            if (effectiveScopeProjectId && projects.length === 0) return null;
            return { ...business, projects };
          })
          .filter(Boolean);
        if ((effectiveScopeBusinessId || effectiveScopeProjectId) && businesses.length === 0) return null;
        return { ...company, businesses };
      })
      .filter(Boolean);
  }, [effectiveScopeBusinessId, effectiveScopeCompanyId, effectiveScopeProjectId, hierarchy]);

  const scopedCompanies = scopedHierarchy;
  const scopedBusinesses = useMemo(() => scopedHierarchy.flatMap((company) => company.businesses || []), [scopedHierarchy]);
  const scopedProjects = useMemo(() => scopedBusinesses.flatMap((business) => business.projects || []), [scopedBusinesses]);

  const scopedTaskBoard = useMemo(() => {
    return taskBoard
      .map((task) => {
        const project = projectLookup[task.project_id] || {};
        return {
          ...task,
          business_id: project.business_id || "",
          company_id: project.company_id || "",
        };
      })
      .filter((task) => matchesPortfolioScope(task.company_id, task.business_id, task.project_id));
  }, [matchesPortfolioScope, projectLookup, taskBoard]);

  const scopedDashboardData = useMemo(() => {
    if (!dashboardData) return null;
    const totalBudget = scopedProjects.reduce((sum, project) => sum + Number(project.budget || 0), 0);
    const statusCounter = {};
    const companyTotals = {};
    for (const project of scopedProjects) {
      const statusValue = project.status || "Unknown";
      statusCounter[statusValue] = (statusCounter[statusValue] || 0) + 1;
      companyTotals[project.company_id] = (companyTotals[project.company_id] || 0) + Number(project.budget || 0);
    }
    const doneCount = scopedTaskBoard.filter((task) => task.status === "Done").length;
    const overdueTasks = scopedTaskBoard.filter((task) => task.is_overdue).sort((a, b) => String(a.due_date || "").localeCompare(String(b.due_date || "")));
    const upcomingDeadlines = scopedTaskBoard
      .filter((task) => !task.is_overdue && task.due_date && task.status !== "Done")
      .sort((a, b) => String(a.due_date || "").localeCompare(String(b.due_date || "")));
    const unassignedTasks = scopedTaskBoard.filter((task) => !task.assignee);
    const blockedProjects = scopedProjects.filter((project) => project.status === "Blocked").map((project) => ({
      project_id: project.id,
      project_name: project.name,
      company_name: project.company_name,
      project_status: project.status,
      lead: project.lead,
    }));
    const priorityProjects = scopedProjects
      .map((project) => {
        const projectTasks = scopedTaskBoard.filter((task) => task.project_id === project.id);
        const openTasks = projectTasks.filter((task) => task.status !== "Done");
        const overdueProjectTasks = openTasks.filter((task) => task.is_overdue);
        return {
          project_id: project.id,
          project_name: project.name,
          company_name: project.company_name,
          project_status: project.status,
          lead: project.lead,
          overdue_task_count: overdueProjectTasks.length,
          open_task_count: openTasks.length,
        };
      })
      .filter((item) => item.overdue_task_count > 0 || item.open_task_count > 0)
      .sort((a, b) => (b.overdue_task_count - a.overdue_task_count) || (b.open_task_count - a.open_task_count) || a.project_name.localeCompare(b.project_name))
      .slice(0, 8);
    const workload = Object.values(scopedTaskBoard.reduce((acc, task) => {
      const key = task.assignee || "Unassigned";
      if (!acc[key]) {
        acc[key] = {
          member_name: key,
          active_tasks: 0,
          overdue_tasks: 0,
          blocked_tasks: 0,
          projects: 0,
          known_team_member: key !== "Unassigned",
          project_ids: new Set(),
        };
      }
      const bucket = acc[key];
      if (task.status !== "Done") bucket.active_tasks += 1;
      if (task.is_overdue) bucket.overdue_tasks += 1;
      if (task.status === "Blocked") bucket.blocked_tasks += 1;
      if (task.project_id) bucket.project_ids.add(task.project_id);
      return acc;
    }, {})).map((item) => ({
      member_name: item.member_name,
      active_tasks: item.active_tasks,
      overdue_tasks: item.overdue_tasks,
      blocked_tasks: item.blocked_tasks,
      projects: item.project_ids.size,
      known_team_member: item.known_team_member,
      load_score: item.active_tasks + (item.overdue_tasks * 2) + item.blocked_tasks,
    })).sort((a, b) => (b.load_score - a.load_score) || (b.active_tasks - a.active_tasks) || a.member_name.localeCompare(b.member_name)).slice(0, 8);
    const budgetHotspots = Object.entries(companyTotals)
      .map(([companyId, total]) => ({
        company_id: companyId,
        company_name: companyLookup[companyId]?.name || "Unknown",
        total_budget: total,
        share_of_budget: totalBudget ? Math.round((total / totalBudget) * 100) : 0,
      }))
      .sort((a, b) => b.total_budget - a.total_budget)
      .slice(0, 6);
    const completionRate = scopedTaskBoard.length ? Math.round((doneCount / scopedTaskBoard.length) * 100) : 0;
    const overdueRate = scopedTaskBoard.length ? Math.round((overdueTasks.length / scopedTaskBoard.length) * 100) : 0;
    const unassignedRate = scopedTaskBoard.length ? Math.round((unassignedTasks.length / scopedTaskBoard.length) * 100) : 0;
    const healthScore = Math.max(0, Math.min(100, 100 - overdueRate - Math.round(unassignedRate / 2) - (blockedProjects.length * 5) + Math.round(completionRate / 4)));
    const smartReport = {
      generated_at: dashboardData.smart_report?.generated_at,
      ai: dashboardData.smart_report?.ai || { configured: false, model: "" },
      ai_brief: hasPortfolioScope ? null : dashboardData.smart_report?.ai_brief,
      ai_brief_error: hasPortfolioScope ? "AI brief remains portfolio-wide. Clear the scope to view the global AI brief." : dashboardData.smart_report?.ai_brief_error,
      portfolio_health: {
        score: healthScore,
        label: healthScore >= 80 ? "Healthy" : healthScore >= 60 ? "Watch" : healthScore >= 40 ? "At Risk" : "Critical",
        completion_rate: completionRate,
        overdue_rate: overdueRate,
        unassigned_rate: unassignedRate,
      },
      totals: {
        companies: scopedCompanies.length,
        businesses: scopedBusinesses.length,
        projects: scopedProjects.length,
        tasks: scopedTaskBoard.length,
        team_members: teamMembers.length,
        overdue_tasks: overdueTasks.length,
        unassigned_tasks: unassignedTasks.length,
        blocked_projects: blockedProjects.length,
      },
      insights: [
        overdueTasks.length ? { severity: "high", title: "Overdue work needs attention", detail: `${overdueTasks.length} scoped task${overdueTasks.length === 1 ? " is" : "s are"} overdue.` } : null,
        priorityProjects[0] ? { severity: "medium", title: "Priority project pressure is visible", detail: `${priorityProjects[0].project_name} leads the scoped workload with ${priorityProjects[0].open_task_count} open tasks.` } : null,
        budgetHotspots[0] ? { severity: "low", title: "Budget concentration is visible", detail: `${budgetHotspots[0].company_name} represents ${budgetHotspots[0].share_of_budget}% of the scoped budget.` } : null,
      ].filter(Boolean),
      upcoming_deadlines: upcomingDeadlines.slice(0, 8),
      overdue_tasks: overdueTasks.slice(0, 8),
      blocked_projects: blockedProjects.slice(0, 6),
      budget_hotspots: budgetHotspots,
      workload,
      priority_projects: priorityProjects,
    };
    return {
      ...dashboardData,
      total_companies: scopedCompanies.length,
      total_businesses: scopedBusinesses.length,
      total_projects: scopedProjects.length,
      total_budget: totalBudget,
      projects_by_status: Object.entries(statusCounter).map(([status, count]) => ({ status, count })),
      budget_by_company: Object.entries(companyTotals).map(([companyId, total]) => ({ company_id: companyId, company_name: companyLookup[companyId]?.name || "Unknown", total_budget: total })),
      smart_report: smartReport,
    };
  }, [companyLookup, dashboardData, hasPortfolioScope, scopedCompanies, scopedProjects, scopedBusinesses, scopedTaskBoard, teamMembers.length]);

  const scopedOpsDashboardData = useMemo(() => {
    const scopedSiteRows = opsSites.filter((site) => {
      const business = businessLookup[site.business_id] || {};
      return matchesPortfolioScope(business.company_id || "", site.business_id, effectiveScopeProjectId);
    });
    const siteIds = new Set(scopedSiteRows.map((site) => site.id));
    const scopedProcurement = opsProcurement.filter((request) => siteIds.has(request.site_id));
    const scopedAssets = opsAssets.filter((item) => siteIds.has(item.site_id));
    const scopedZones = opsZones.filter((item) => siteIds.has(item.site_id));
    const scopedSessions = opsSessions.filter((item) => siteIds.has(item.site_id));
    const scopedPayments = opsPayments.filter((item) => siteIds.has(item.site_id));
    const scopedMaintenance = opsMaintenance.filter((item) => siteIds.has(item.site_id));
    const scopedInventory = opsInventory.filter((item) => siteIds.has(item.site_id));
    const scopedSuppliers = opsSuppliers.filter((item) => {
      const business = businessLookup[item.business_id] || {};
      return matchesPortfolioScope(business.company_id || "", item.business_id, effectiveScopeProjectId);
    });
    const lines = scopedProcurement.flatMap((request) => request.lines || []);
    const quotes = lines.flatMap((line) => line.quotes || []);
    const lowStockItems = scopedInventory.filter((item) => Number(item.quantity_on_hand || 0) <= Number(item.reorder_level || 0));
    const openWorkOrders = scopedMaintenance.filter((item) => !["Completed", "Closed"].includes(item.status));
    const criticalMissingQuotes = lines.filter((line) => line.is_critical && !(line.quotes || []).some((quote) => quote.quote_received));
    const revenueTotal = scopedPayments.reduce((sum, item) => sum + (item.status === "Failed" ? 0 : Number(item.amount || 0)), 0);
    const upcomingMaintenance = openWorkOrders
      .map((item) => {
        const parsed = item.scheduled_date ? new Date(item.scheduled_date) : null;
        const today = new Date();
        const daysUntil = parsed && !Number.isNaN(parsed.getTime()) ? Math.floor((parsed - today) / (1000 * 60 * 60 * 24)) : null;
        return { ...item, days_until: daysUntil };
      })
      .filter((item) => item.days_until !== null)
      .sort((a, b) => a.days_until - b.days_until)
      .slice(0, 10);
    const siteMetrics = scopedSiteRows.map((site) => {
      const business = businessLookup[site.business_id] || {};
      return {
        site_id: site.id,
        site_name: site.name,
        business_name: business.name || "",
        company_name: companyLookup[business.company_id || ""]?.name || "",
        business_type: business.business_type || "general_ops",
        revenue: scopedPayments.filter((item) => item.site_id === site.id && item.status !== "Failed").reduce((sum, item) => sum + Number(item.amount || 0), 0),
        sessions: scopedSessions.filter((item) => item.site_id === site.id).length,
        zones: scopedZones.filter((item) => item.site_id === site.id).length,
        assets: scopedAssets.filter((item) => item.site_id === site.id).length,
        low_stock_items: lowStockItems.filter((item) => item.site_id === site.id).length,
        open_work_orders: openWorkOrders.filter((item) => item.site_id === site.id).length,
      };
    });
    return {
      ...(opsDashboardData || {}),
      totals: {
        sites: scopedSiteRows.length,
        suppliers: scopedSuppliers.length,
        procurement_requests: scopedProcurement.length,
        assets: scopedAssets.length,
        zones: scopedZones.length,
        sessions: scopedSessions.length,
        payments: scopedPayments.length,
        inventory_items: scopedInventory.length,
        low_stock_items: lowStockItems.length,
        open_work_orders: openWorkOrders.length,
      },
      revenue_total: revenueTotal,
      critical_missing_quotes: criticalMissingQuotes.slice(0, 10),
      low_stock_items: lowStockItems.slice(0, 10),
      upcoming_maintenance: upcomingMaintenance,
      site_metrics: siteMetrics,
    };
  }, [businessLookup, companyLookup, effectiveScopeProjectId, matchesPortfolioScope, opsAssets, opsDashboardData, opsInventory, opsMaintenance, opsPayments, opsProcurement, opsSessions, opsSites, opsSuppliers, opsZones]);

  const selectedCompany = hierarchy.find((company) => company.id === selectedCompanyId);
  const selectedBusiness = selectedCompany?.businesses?.find((business) => business.id === selectedBusinessId);
  const selectedProject = selectedBusiness?.projects?.find((project) => project.id === selectedProjectId);
  const selectedSite = (selectedBusiness?.ops_sites || []).find((site) => site.id === selectedSiteId) || opsSites.find((site) => site.id === selectedSiteId);
  const projectActivity = activity.filter((item) => item.project_id === selectedProjectId);
  const unreadNotificationCount = notifications.filter((item) => !item.is_read).length;
  const canEdit = currentUser?.role === "admin" || currentUser?.role === "manager";
  const canManageTeam = currentUser?.role === "admin";
  const showPortfolioScopeToolbar = view === "dashboard" || view === "tasks" || view === "ops-dashboard";

  const renderContent = () => {
    if (loading) {
      return <div className="empty-state"><div className="text-lg font-mono">Loading...</div></div>;
    }

    if (view === "search" && searchResults) {
      return <SearchResults onSelectBusiness={handleSelectBusiness} onSelectCompany={handleSelectCompany} onSelectOpsEntity={handleSelectOpsEntity} onSelectProject={handleSelectProject} onSelectSite={handleSelectSite} query={searchQuery} results={searchResults} />;
    }

    if (view === "tasks") {
      return <TaskWorkspace currentUser={currentUser} exportProjectsUrl={`${API}/exports/projects.csv`} exportTasksUrl={`${API}/exports/tasks.csv`} filter={taskFilter} onChangeFilter={setTaskFilter} onOpenTask={handleOpenDashboardProject} tasks={scopedTaskBoard} />;
    }

    if (view === "ops-dashboard") {
      return (
        <OpsDashboard
          data={scopedOpsDashboardData}
          onOpenSite={(siteId) => {
            const site = opsSites.find((item) => item.id === siteId);
            if (!site) return;
            for (const company of hierarchy) {
              for (const business of company.businesses || []) {
                if (business.id === site.business_id) {
                  handleSelectSite(site.id, business.id, company.id);
                  return;
                }
              }
            }
          }}
        />
      );
    }

    if (view === "ops-site" && selectedSite && selectedBusiness) {
      return (
        <OpsSiteDetail
          assets={opsAssets}
          business={selectedBusiness}
          inventory={opsInventory}
          maintenance={opsMaintenance}
          memberships={opsMemberships}
          onBack={() => handleSelectBusiness(selectedBusinessId, selectedCompanyId)}
          onCreateAsset={handleCreateOpsAsset}
          onCreateInventoryItem={handleCreateOpsInventoryItem}
          onCreateInventoryMovement={handleCreateOpsInventoryMovement}
          onCreateMaintenanceLog={handleCreateOpsMaintenanceLog}
          onCreateProcurementLine={handleCreateOpsProcurementLine}
          onCreateSupplierQuote={handleCreateOpsSupplierQuote}
          onCreateMaintenance={handleCreateOpsMaintenance}
          onCreateMembership={handleCreateOpsMembership}
          onCreatePayment={handleCreateOpsPayment}
          onCreateProcurement={handleCreateOpsProcurement}
          onCreateService={handleCreateOpsService}
          onCreateSession={handleCreateOpsSession}
          onCreateSupplier={handleCreateOpsSupplier}
          onCreateZone={handleCreateOpsZone}
          onDeleteAsset={handleDeleteOpsAsset}
          onDeleteInventoryItem={handleDeleteOpsInventoryItem}
          onDeleteInventoryMovement={handleDeleteOpsInventoryMovement}
          onDeleteMaintenance={handleDeleteOpsMaintenance}
          onDeleteMaintenanceLog={handleDeleteOpsMaintenanceLog}
          onDeleteMembership={handleDeleteOpsMembership}
          onDeletePayment={handleDeleteOpsPayment}
          onDeleteProcurement={handleDeleteOpsProcurement}
          onDeleteProcurementLine={handleDeleteOpsProcurementLine}
          onDeleteService={handleDeleteOpsService}
          onDeleteSession={handleDeleteOpsSession}
          onDeleteSupplier={handleDeleteOpsSupplier}
          onDeleteSupplierQuote={handleDeleteOpsSupplierQuote}
          onDeleteZone={handleDeleteOpsZone}
          onUpdateAsset={handleUpdateOpsAsset}
          onUpdateInventoryItem={handleUpdateOpsInventoryItem}
          onUpdateInventoryMovement={handleUpdateOpsInventoryMovement}
          onUpdateMaintenance={handleUpdateOpsMaintenance}
          onUpdateMaintenanceLog={handleUpdateOpsMaintenanceLog}
          onUpdateMembership={handleUpdateOpsMembership}
          onUpdatePayment={handleUpdateOpsPayment}
          onUpdateProcurement={handleUpdateOpsProcurement}
          onUpdateProcurementLine={handleUpdateOpsProcurementLine}
          onUpdateService={handleUpdateOpsService}
          onUpdateSession={handleUpdateOpsSession}
          onUpdateSupplier={handleUpdateOpsSupplier}
          onUpdateSupplierQuote={handleUpdateOpsSupplierQuote}
          onUpdateZone={handleUpdateOpsZone}
          payments={opsPayments}
          procurement={opsProcurement}
          services={opsServices}
          sessions={opsSessions}
          site={selectedSite}
          suppliers={opsSuppliers.filter((item) => item.business_id === selectedBusiness.id)}
          zones={opsZones}
          initialEntityId={selectedOpsEntityId}
          initialEntityType={selectedOpsEntityType}
        />
      );
    }

    if (view === "project" && selectedProject) {
      return (
        <ProjectDetail
          activity={projectActivity}
          business={selectedBusiness}
          company={selectedCompany}
          currentUser={currentUser}
          exportProjectsUrl={`${API}/exports/projects.csv`}
          exportTasksUrl={`${API}/exports/tasks.csv`}
          onBack={() => handleSelectBusiness(selectedBusinessId, selectedCompanyId)}
          onClearTaskSelection={() => setSelectedTaskId(null)}
          onCreateComment={handleCreateComment}
          onCreateShareLink={handleCreateShareLink}
          onCreateSubtask={handleCreateSubtask}
          onCreateTask={handleCreateTask}
          onDelete={() => handleDeleteProject(selectedProject.id)}
          onDeleteAttachment={handleDeleteAttachment}
          onDeleteComment={handleDeleteComment}
          onDeleteSubtask={handleDeleteSubtask}
          onDeleteTask={handleDeleteTask}
          onEdit={() => setEditingProject(selectedProject)}
          onGenerateProjectSummary={handleGenerateProjectSummary}
          onUpdateComment={handleUpdateComment}
          onUpdateSubtask={handleUpdateSubtask}
          onUpdateTask={handleUpdateTask}
          onUploadAttachment={handleUploadAttachment}
          project={selectedProject}
          selectedTaskId={selectedTaskId}
          teamMembers={teamMembers}
        />
      );
    }

    if (view === "business" && selectedBusiness) {
      return <BusinessDetail business={selectedBusiness} company={selectedCompany} onAddProject={canEdit ? () => setShowProjectForm(true) : undefined} onBack={() => handleSelectCompany(selectedCompanyId)} onDelete={canEdit ? () => handleDeleteBusiness(selectedBusiness.id) : undefined} onEdit={canEdit ? () => setEditingBusiness(selectedBusiness) : undefined} onSelectProject={(projectId) => handleSelectProject(projectId, selectedBusiness.id, selectedCompany.id)} onSelectSite={(siteId) => handleSelectSite(siteId, selectedBusiness.id, selectedCompany.id)} />;
    }

    if (view === "company" && selectedCompany) {
      return <CompanyDetail company={selectedCompany} onAddBusiness={canEdit ? () => setShowBusinessForm(true) : undefined} onDelete={canEdit ? () => handleDeleteCompany(selectedCompany.id) : undefined} onEdit={canEdit ? () => setEditingCompany(selectedCompany) : undefined} onSelectBusiness={(businessId) => handleSelectBusiness(businessId, selectedCompany.id)} />;
    }

    return <Dashboard aiBriefLoading={aiBriefLoading} data={scopedDashboardData} onGenerateAiBrief={handleGenerateAiBrief} onOpenProject={handleOpenDashboardProject} />;
  };

  if (publicShareToken) {
    return (
      <>
        <Toaster position="top-right" richColors />
        <SharedProjectView payload={publicShare} />
      </>
    );
  }

  if (authStatus !== "authenticated") {
    return (
      <>
        <Toaster position="top-right" richColors />
        <LoginGate loading={authLoading} onLogin={handleLogin} />
      </>
    );
  }

  return (
    <div className="app-container" data-testid="app-container">
      <Toaster position="top-right" richColors />
      <Sidebar
        hierarchy={hierarchy}
        isOpen={sidebarOpen}
        isOpsView={view === "ops-dashboard" || view === "ops-site"}
        isTaskView={view === "tasks"}
        onGoToDashboard={handleGoToDashboard}
        onGoToOps={handleGoToOps}
        onGoToTasks={handleGoToTasks}
        onSelectBusiness={handleSelectBusiness}
        onSelectCompany={handleSelectCompany}
        onSelectProject={handleSelectProject}
        onSelectSite={handleSelectSite}
        selectedBusinessId={selectedBusinessId}
        selectedCompanyId={selectedCompanyId}
        selectedProjectId={selectedProjectId}
        selectedSiteId={selectedSiteId}
      />
      {sidebarOpen ? <button className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} type="button" /> : null}
      <div className="main-panel">
        <button className="session-logout-button" onClick={handleLogout} type="button">Log Out</button>
        <TopBar
          canEdit={canEdit}
          canManageTeam={canManageTeam}
          canManageUsers={currentUser?.role === "admin"}
          hasBusinesses={hierarchy.some((company) => company.businesses?.length > 0)}
          hasCompanies={hierarchy.length > 0}
          notificationCount={unreadNotificationCount}
          onAddBusiness={() => setShowBusinessForm(true)}
          onAddCompany={() => setShowCompanyForm(true)}
          onAddOpsSite={() => {
            if (!selectedBusinessId) {
              toast.error("Select a business first to add an operations site.");
              return;
            }
            const business = hierarchy.flatMap((company) => company.businesses || []).find((item) => item.id === selectedBusinessId);
            const siteType = {
              car_wash: "wash_site",
              campsite: "camp",
              hotel: "hotel",
              automotive_parts: "branch",
              manufacturing: "factory",
              general_ops: "general_site",
            }[business?.business_type || "general_ops"];
            const name = window.prompt("Operations site name");
            if (name) {
              handleCreateOpsSite({ business_id: selectedBusinessId, name, site_type: siteType, location: "", manager_name: "", status: "Active", operating_hours: "", currency: "USD", timezone: "UTC", configuration: {} });
            }
          }}
          onAddProject={() => setShowProjectForm(true)}
          onAddTeamMember={() => canManageTeam ? setShowTeamMemberManager(true) : null}
          onGoToOps={handleGoToOps}
          onGoToTasks={handleGoToTasks}
          onOpenNotifications={() => setShowNotifications(true)}
          onOpenUsers={() => setShowUserManager(true)}
          onSearch={handleSearch}
          onToggleSidebar={() => setSidebarOpen((current) => !current)}
          searchQuery={searchQuery}
        />
        <div className="content-area">
          {showPortfolioScopeToolbar ? (
            <ScopeToolbar
              hierarchy={hierarchy}
              onBusinessChange={handleScopeBusinessChange}
              onClear={clearPortfolioScope}
              onCompanyChange={handleScopeCompanyChange}
              onProjectChange={handleScopeProjectChange}
              scopeBusinessId={scopeBusinessId}
              scopeCompanyId={scopeCompanyId}
              scopeProjectId={scopeProjectId}
            />
          ) : null}
          {renderContent()}
        </div>
      </div>

      {canEdit ? <CompanyForm onClose={() => setShowCompanyForm(false)} onSubmit={handleCreateCompany} open={showCompanyForm} /> : null}
      {canEdit ? <CompanyForm initialData={editingCompany} isEdit onClose={() => setEditingCompany(null)} onSubmit={(data) => handleUpdateCompany(editingCompany?.id, data)} open={!!editingCompany} /> : null}
      {canEdit ? <BusinessForm companies={hierarchy} onClose={() => setShowBusinessForm(false)} onSubmit={handleCreateBusiness} open={showBusinessForm} preselectedCompanyId={selectedCompanyId} /> : null}
      {canEdit ? <BusinessForm companies={hierarchy} initialData={editingBusiness} isEdit onClose={() => setEditingBusiness(null)} onSubmit={(data) => handleUpdateBusiness(editingBusiness?.id, data)} open={!!editingBusiness} /> : null}
      {canEdit ? <ProjectForm hierarchy={hierarchy} onClose={() => setShowProjectForm(false)} onSubmit={handleCreateProject} open={showProjectForm} preselectedBusinessId={selectedBusinessId} preselectedCompanyId={selectedCompanyId} teamMembers={teamMembers} /> : null}
      {canEdit ? <ProjectForm hierarchy={hierarchy} initialData={editingProject} isEdit onClose={() => setEditingProject(null)} onSubmit={(data) => handleUpdateProject(editingProject?.id, data)} open={!!editingProject} teamMembers={teamMembers} /> : null}
      {canManageTeam ? <TeamMemberManager members={teamMembers} onClose={() => setShowTeamMemberManager(false)} onCreate={handleCreateTeamMember} onDelete={handleDeleteTeamMember} onUpdate={handleUpdateTeamMember} open={showTeamMemberManager} /> : null}
      {currentUser?.role === "admin" ? <UserManager companies={hierarchy} onClose={() => setShowUserManager(false)} onCreate={handleCreateUser} onDelete={handleDeleteUser} onUpdate={handleUpdateUser} open={showUserManager} users={users} /> : null}
      <NotificationCenter notifications={notifications} onClose={() => setShowNotifications(false)} onMarkRead={handleMarkNotificationRead} onOpenTask={handleOpenDashboardProject} open={showNotifications} />
    </div>
  );
}

export default App;
